// drawUtils.ts

import * as d3 from 'd3';
import { ICustomPatternData } from '../types/ICustomPatternData';



export function drawRect(
    group: d3.Selection<SVGGElement, any, any, any>,
    x: number,
    y: number,
    width: number,
    height: number,
    stroke: string,
    fill: string,
    strokeWidth: number,
    opacity?: number
): void {
    const rect = group
        .append('rect')
        .attr('x', x)
        .attr('y', y)
        .attr('width', width)
        .attr('height', height)
        .style('fill', fill)
        .style('stroke', stroke)
        .style('stroke-width', strokeWidth)


    if (opacity !== undefined) {
        rect.style('opacity', opacity);
    }

}

export function drawArc(
    group: d3.Selection<SVGGElement, any, any, any>,
    cx: number,
    cy: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    stroke: string,
    fill: string,
    opacity: number
): void {
    group.append('path')
        .attr('d', d3.arc()
            .innerRadius(radius)
            .outerRadius(radius)
            .startAngle(startAngle)
            .endAngle(endAngle)
        )
        .attr('transform', `translate(${cx}, ${cy})`)
        .style('fill', fill)
        .style('stroke', stroke)
        .style('opacity', opacity);
};

export function drawCircle(
    group: d3.Selection<SVGGElement, any, any, any>,
    cx: number,
    cy: number,
    r: number,
    fill: string,
    opacity: number,
    stroke?: string,
    strokeWidth?: number,
): void {
    const circle = group.append('circle')
        .attr('cx', cx)
        .attr('cy', cy)
        .attr('r', r)
        .style('fill', fill)
        .style('opacity', opacity);

    // Apply stroke only if provided
    if (stroke !== undefined) {
        circle.style('stroke', stroke);
    }

    // Apply strokeWidth only if provided
    if (strokeWidth !== undefined) {
        circle.style('stroke-width', strokeWidth);
    }

};

export function drawLine(
    group: d3.Selection<SVGGElement, any, any, any>,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    stroke: string,
    strokeWidth: number,
    markerEnd?: string,
    opacity?: number,
): d3.Selection<SVGLineElement, unknown, null, undefined> {
    const line = group.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', stroke)
        .attr('stroke-width', strokeWidth);

    if (markerEnd !== undefined) {
        line.attr('marker-end', markerEnd);
    }

    if (opacity !== undefined) {
        line.style('opacity', opacity);
    }
    return line;
};

export function drawText(
    group: d3.Selection<SVGGElement, any, any, any>,
    x: number,
    y: number,
    dy: number,
    anchor: string,
    fill: string,
    text: string
): void {
    group.append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('dy', dy)
        .attr('text-anchor', anchor)
        .style('fill', fill)
        .text(text);
};

export function drawSymbol(
    group: d3.Selection<SVGGElement, any, any, any>,
    cx: number,
    cy: number,
    symbolType: any,
    fill: string,
    size: number
): void {
    group.append('path')
        .attr('transform', `translate(${cx}, ${cy})`)
        .attr('d', d3.symbol().type(symbolType).size(size)) // Adjust size as needed
        .style('fill', fill);
};

export function drawIcon(
    group: d3.Selection<SVGGElement, any, any, any>,
    cx: number,
    cy: number,
    iconClass: string,
    color: string
): void {
    const iconSize = 20;
    group.append('foreignObject')
        .attr('x', cx - iconSize / 2)
        .attr('y', cy - iconSize / 2)
        .attr('width', iconSize)
        .attr('height', iconSize)
        .html(`<i class="${iconClass}" style="font-size: ${iconSize}px; color: ${color}"></i>`);
};

export function createMarkerPath(
    group: d3.Selection<SVGGElement, any, any, any>,
    color: string
): string {
    const markerId = `arrowhead-${color}`;
    const marker = group.select(`#${markerId}`);

    if (!marker.empty()) {
        // Return existing marker ID if it already exists
        return `#${markerId}`;
    }

    // Create marker if it doesn't exist
    group.append("marker")
        .attr("id", markerId)
        .attr("refX", 6)
        .attr("refY", 3)
        .attr("markerWidth", 13)
        .attr("markerHeight", 13)
        .attr("orient", "auto")
        .append("path")
        .attr("d", "M0,0 L0,6 L9,3 z")
        .style("fill", color);

    return `#${markerId}`;
};

export function drawArrowLine(
    group: d3.Selection<SVGGElement, any, any, any>,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    stroke: string,
    markerColor: string,
    strokeWidth: number
): void {
    group.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', stroke)
        .attr('marker-end', `url(${createMarkerPath(group, markerColor)})`)
        .attr('stroke-width', strokeWidth);
};

export function drawDashedLine(
    group: d3.Selection<SVGGElement, any, any, any>,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    stroke: string,
    strokeDashArray: number,
    strokeWidth: number
): void {
    group.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', stroke)
        .style('stroke-dasharray', strokeDashArray)
        .style('stroke-width', strokeWidth);
};

export function createCustomPattern(
    svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>,
    patternWidth: number,
    patternHeight: number,
    rectanglesData: ICustomPatternData[],
    fillColor?: string
): void {
    const pattern = svg.append('defs')
        .append('pattern')
        .attr('id', 'custom-pattern')
        .attr('width', patternWidth)
        .attr('height', patternHeight)
        .attr('patternUnits', 'userSpaceOnUse');

    pattern.selectAll('rect')
        .data(rectanglesData)
        .enter().append('rect')
        .attr('x', d => d.x)
        .attr('y', d => d.y)
        .attr('width', d => d.width)
        .attr('height', d => d.height)
        .attr('fill', (fillColor || 'white'))
        .attr('opacity', 0.2);
}

export function drawCornerArc(
    group: d3.Selection<SVGGElement, any, any, any>,
    innerRadius: number,
    outerRadius: number,
    startAngle: number,
    endAngle: number,
    x: number,
    y: number,
    fill: string,
    stroke: string,
    opacity: number
): void {
    group.append('path')
        .attr('d', d3.arc()
            .innerRadius(innerRadius)
            .outerRadius(outerRadius) // Consider the stroke width
            .startAngle(startAngle)
            .endAngle(endAngle)
        )
        .attr('transform', `translate(${x}, ${y})`) // Adjust the translation
        .style('fill', fill)
        .style('stroke', stroke)
        .style('opacity', opacity);
}


